import { useState } from 'react';
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateGraphic,
  EmptyStateTitle,
  FormattedDateTime,
  Image,
  Paragraph,
  Strong,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  useSnackbars,
} from '@customerio/pluma-components/react';
import { getUserFacingErrorMessage } from '@/services/api/errors';
import { sqlImportIsScheduled, type SqlImport } from '@/services/db/sql-import/entity';
import type { SqlImportRow } from '@/services/db/sql-import-row/entity';
import type { ObjectType } from '@/services/db/object-type/entity';
import { useCancelSqlImportRow } from '@/services/db/sql-import-row/mutations';
import { SQL_DATABASE_CATALOG } from '@/features/sql-sync';
import type { SqlDatabaseType, SqlImportDatabase } from '@/services/db/sql-import-database/entity';
import { epochNowInSeconds } from '@/utils/time';
import { runTargetNouns, sortRunsNewestFirst } from '../rows';
import { ImportRow } from './import-row';

const CANCEL_FAILED_MESSAGE = "The import couldn't be canceled.";

type ImportRowsProps = {
  workspaceId: number;
  type: SqlDatabaseType;
  sqlImport: SqlImport;
  /** The database the import reads from, when it is still listed; BigQuery runs are named by it. */
  database: SqlImportDatabase | undefined;
  rows: readonly SqlImportRow[];
  /** The workspace's object types, to name what an object import syncs. */
  objectTypes: readonly Pick<ObjectType, 'id' | 'name' | 'singular_name'>[];
  /** Segment names by id, for the runs that filled one. */
  segmentNames: ReadonlyMap<string, string>;
  isReadOnly: boolean;
};

/** The import's runs, newest first, each expandable; or why there are none yet. */
export function ImportRows({
  workspaceId,
  type,
  sqlImport,
  database,
  rows,
  objectTypes,
  segmentNames,
  isReadOnly,
}: ImportRowsProps) {
  const snackbars = useSnackbars();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const cancel = useCancelSqlImportRow({ workspaceId });
  const targetNouns = runTargetNouns(sqlImport, objectTypes, { singular: 'person', plural: 'people' });
  const rowSyncTarget = targetNouns.plural;
  const isScheduled = sqlImportIsScheduled(sqlImport, epochNowInSeconds());

  if (rows.length === 0 && !isScheduled) {
    return (
      <EmptyState data-test-sql-import-rows-empty>
        <EmptyStateGraphic>
          <Image src={SQL_DATABASE_CATALOG[type].imageSrc} alt="" size="icon-v2-xl" />
        </EmptyStateGraphic>
        <EmptyStateTitle>Sync {rowSyncTarget} from your database</EmptyStateTitle>
        <EmptyStateDescription>
          {sqlImport.active ? (
            <>
              <Strong>{sqlImport.name}</Strong> is importing {rowSyncTarget} to your workspace…
            </>
          ) : (
            <>
              Activate <Strong>{sqlImport.name}</Strong> to start importing people to your workspace.
            </>
          )}
        </EmptyStateDescription>
      </EmptyState>
    );
  }

  /** Cancels a run, reporting a failure in a snackbar; resolves to whether it went through. */
  const cancelRow = async (row: SqlImportRow): Promise<boolean> => {
    try {
      await cancel.mutateAsync({ rowId: row.id });
      return true;
    } catch (error) {
      snackbars.create({
        id: 'sqlImports.cancelImport.error',
        variant: 'critical',
        message: getUserFacingErrorMessage(error, CANCEL_FAILED_MESSAGE),
      });
      return false;
    }
  };

  return (
    <Table caption="Import history" data-test-sql-import-rows>
      <TableThead>
        <TableTr>
          <TableTh>Imports</TableTh>
          <TableTh>Status</TableTh>
          <TableTh textAlign="right">Timestamp</TableTh>
        </TableTr>
      </TableThead>
      <TableTbody>
        {rows.length === 0 ? (
          <TableTr>
            <TableTd colSpan={3}>
              <Paragraph color="subtle" textAlign="center" my="300">
                Sync is scheduled to start on{' '}
                <Strong>
                  <FormattedDateTime date={sqlImport.start_at * 1000} />
                </Strong>
              </Paragraph>
            </TableTd>
          </TableTr>
        ) : (
          sortRunsNewestFirst(rows).map((row) => (
            <ImportRow
              key={row.id}
              row={row}
              database={database}
              workspaceId={workspaceId}
              targetNouns={targetNouns}
              segmentName={segmentNames.get(row.segment_id)}
              isExpanded={expandedId === row.id}
              isReadOnly={isReadOnly}
              isCanceling={cancel.isPending && cancel.variables?.rowId === row.id}
              onToggle={() => setExpandedId((current) => (current === row.id ? null : row.id))}
              onCancel={() => cancelRow(row)}
            />
          ))
        )}
      </TableTbody>
    </Table>
  );
}
